import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Modal, Button, Checkbox, Input, Row, Col } from 'antd';
import styled from 'styled-components';
import aptSelectors from 'services/selectors/apt';
import { CUSTOMER_DETAIL_MODAL_INDEX } from 'constants/variables';

const { TextArea } = Input;

export type CustomerSurveyModalRef = {
  open: (value?: ICustomerSurveyValue) => Promise<ICustomerSurveyValue>;
  close: () => void;
};

export interface ICustomerSurveyValue {
  options: any[];
  others: string;
}

const CustomerSurveyModal = forwardRef<CustomerSurveyModalRef>(({ }, ref) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<any[]>([]);
  const [others, setOthers] = useState('');
  const callbackResolve = useRef<(data: ICustomerSurveyValue) => void>(() => undefined);

  const surveys = aptSelectors.getShopSurveyInfo();

  const handleOpen = (value?: ICustomerSurveyValue) => {
    if (value) {
      setSelected(value.options || []);
      setOthers(value.others || '');
    } else {
      setSelected([]);
      setOthers('');
    }

    setOpen(true);

    return new Promise<ICustomerSurveyValue>((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const toggleOption = (item: any) => {
    const isSelected = selected.some(i => i.id === item.id);

    if (isSelected) {
      setSelected(prev => prev.filter(i => i.id !== item.id));
    } else {
      setSelected(prev => [...prev, item]);
    }
  };

  const handleSubmit = () => {
    callbackResolve.current({
      options: selected,
      others
    });
    handleClose();
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={800}
      title="Select"
      zIndex={CUSTOMER_DETAIL_MODAL_INDEX + 1}
    >
      <Container>
        <Description>
          Please select any of the following medical or skin conditions that apply
        </Description>

        <Row gutter={[16, 16]}>
          {(surveys ?? []).map((item) => {
            const isChecked = selected.some(i => i.id === item.id);

            return (
              <Col span={8} key={item.id}>
                <OptionItem>
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggleOption(item)}
                  />
                  <span>{item.option}</span>
                </OptionItem>
              </Col>
            );
          })}
        </Row>

        <OtherLabel>Others</OtherLabel>

        <TextArea
          rows={4}
          placeholder="Please enter..."
          value={others}
          onChange={(e) => setOthers(e.target.value)}
        />
      </Container>

      <div style={{ borderTop: '1px solid #eee', padding: 16, textAlign: 'right' }}>
        <Button type="primary" onClick={handleSubmit}>
          OK
        </Button>
      </div>
    </Modal>
  );
});

export default CustomerSurveyModal;


const Container = styled.div`
  padding: 20px;
  max-height: 600px;
  overflow-y: auto;
`;

const Description = styled.div`
  text-align: center;
  margin-bottom: 24px;
  font-size: 16px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OtherLabel = styled.div`
  margin-top: 24px;
  margin-bottom: 8px;
  font-weight: 600;
`;